import { useCallback, useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useAuth } from '@contexts/AuthContext';
import { useProId } from '@contexts/ProIdContext';
import { GET_JOB_APPLICATIONS_UPERCUT, GET_JOB_APPLICATIONS_PRO_V2 } from '@gql/jobs';
import { IJobApplicationDetailFilter } from '@modules/jobApplications/JobApplicationFilter';
import { KanbanData } from '@modules/jobApplications/types';
import { JobApplicationStatus } from '@modules/jobApplications/statuses';

const ITEMS_PER_PAGE = 10;

// Define all kanban columns
const KANBAN_COLUMNS = [
    JobApplicationStatus.New,
    JobApplicationStatus.Interview,
    JobApplicationStatus.Trial,
    JobApplicationStatus.Offer,
    JobApplicationStatus.ProbationPeriod,
    JobApplicationStatus.Hired,
    JobApplicationStatus.Rejected,
];

/**
 * Hook to fetch job applications for kanban view with infinite scroll per status column
 * 
 * This hook manages separate pagination state for each status column and provides
 * a mechanism to load more items per column as the user scrolls.
 * 
 * @param query - Search query string
 * @param filter - Filter options for job applications (excluding status)
 * @param sortBy - Sort field name
 */
export const useGetJobApplicationsKanban = (
    query: string = '',
    filter: IJobApplicationDetailFilter = {},
    sortBy: { field: string; order: number } = { field: 'date', order: -1 },
    limit: number = 10
) => {
    const { user, isUpercut } = useAuth();
    const { isProIdRequired } = useProId();

    // State for kanban data organized by status
    const [kanbanData, setKanbanData] = useState<KanbanData>({});

    // Select the appropriate query based on user role
    const queryToUse = isUpercut && !isProIdRequired
        ? GET_JOB_APPLICATIONS_UPERCUT
        : GET_JOB_APPLICATIONS_PRO_V2;

    // Use lazy query to fetch per status
    const [fetchJobApplications] = useLazyQuery(queryToUse, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'network-only',
    });

    /**
     * Initialize kanban columns with empty data
     */
    const initializeKanbanData = useCallback(() => {
        const initialData: KanbanData = {};
        KANBAN_COLUMNS.forEach(status => {
            initialData[status] = {
                items: [],
                hasMore: false,
                loading: false,
                page: 1,
                totalDocs: 0,
            };
        });
        setKanbanData(initialData);
    }, []);

    /**
     * Fetch data for a specific status column
     */
    const fetchColumnData = useCallback(
        async (status: string, page: number = 1) => {
            // Set loading state for this column
            setKanbanData(prev => ({
                ...prev,
                [status]: {
                    ...prev[status],
                    loading: true,
                },
            }));

            try {
                const { data, error } = await fetchJobApplications({
                    variables: {
                        query,
                        filter: {
                            ...filter,
                            yearsOfExperience:
                                filter.yearsOfExperience?.min != null || filter.yearsOfExperience?.max != null
                                    ? { min: filter.yearsOfExperience.min, max: filter.yearsOfExperience.max }
                                    : undefined,       
                            status: status, // Filter by specific status (backend expects 'status', not 'jobApplicationStatus')
                        },
                        sortBy,
                        pagination: {
                            page,
                            limit,
                        },
                    },
                });

                if (error) {
                    console.error(`Error fetching ${status} applications:`, error);
                    setKanbanData(prev => ({
                        ...prev,
                        [status]: {
                            ...prev[status],
                            loading: false,
                        },
                    }));
                    return;
                }

                // Extract data based on which query was used
                const jobApplicationsData = isUpercut && !isProIdRequired
                    ? data?.getJobApplicationsUpercut
                    : data?.getJobApplicationsProV2;

                if (jobApplicationsData) {
                    setKanbanData(prev => {
                        const existingItems = page === 1 ? [] : prev[status]?.items || [];
                        return {
                            ...prev,
                            [status]: {
                                items: [...existingItems, ...jobApplicationsData.docs],
                                hasMore: jobApplicationsData.meta.hasNextPage,
                                loading: false,
                                page,
                                totalDocs: jobApplicationsData.meta.totalDocs,
                            },
                        };
                    });
                }
            } catch (err) {
                console.error(`Error fetching ${status} applications:`, err);
                setKanbanData(prev => ({
                    ...prev,
                    [status]: {
                        ...prev[status],
                        loading: false,
                    },
                }));
            }
        },
        [fetchJobApplications, query, filter, sortBy, isUpercut, isProIdRequired]
    );

    /**
     * Load more items for a specific status column (infinite scroll)
     */
    const loadMore = useCallback(
        (status: string) => {
            const columnData = kanbanData[status];
            if (!columnData || columnData.loading || !columnData.hasMore) {
                return;
            }

            const nextPage = columnData.page + 1;
            fetchColumnData(status, nextPage);
        },
        [kanbanData, fetchColumnData]
    );

    /**
     * Refetch all columns (useful when filters/search changes)
     */
    const refetchAll = useCallback(() => {
        initializeKanbanData();
        KANBAN_COLUMNS.forEach(status => {
            fetchColumnData(status, 1);
        });
    }, [initializeKanbanData, fetchColumnData]);

    /**
     * Initial load: fetch first page for all status columns
     */
    useEffect(() => {
        if (!user) return;

        initializeKanbanData();
        KANBAN_COLUMNS.forEach(status => {
            fetchColumnData(status, 1);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]); // Refetch when search/filter changes

    // Calculate total loading state (true if any column is loading)
    const loading = Object.values(kanbanData).some(col => col.loading);

    // Calculate total documents across all statuses
    const totalDocs = Object.values(kanbanData).reduce((sum, col) => sum + (col.totalDocs || 0), 0);

    return {
        kanbanData,
        loading,
        totalDocs,
        loadMore,
        refetchAll,
        isUpercut,
        userRole: user?.role,
    };
};
